// ═══════════════════════════════════════════════════════════════════════════
// ICON REGISTRY
// Maps icon string names from API to Lucide React components
// This enables dynamic sidebar rendering from database configuration
// ═══════════════════════════════════════════════════════════════════════════

import {
  // Navigation & Layout
  LayoutDashboard,
  LayoutGrid,
  Menu,
  Home,

  // Commerce
  ShoppingCart,
  ShoppingBag,
  CreditCard,
  DollarSign,
  Receipt,
  Percent,
  Award,
  Crown,
  Undo2,

  // Inventory & Products
  Package,
  PackageCheck,
  Boxes,
  Pill,
  FlaskConical,
  ClipboardList,
  ClipboardCheck,
  SlidersHorizontal,
  CalendarClock,
  AlertTriangle as TriangleAlert,

  // Documents & Files
  FileText,
  File,
  Files,
  Folder,
  ScrollText,
  FileUp,

  // People & Organizations
  Users,
  UserCog,
  User,
  Building,
  Building2,
  Factory,
  Store,
  Stethoscope,

  // Transport & Logistics
  Truck,
  ArrowRightLeft,
  MapPin,

  // Charts & Reports
  BarChart,
  BarChart3,
  TrendingUp,
  PieChart,

  // Settings & Admin
  Settings,
  Shield,
  ShieldCheck,
  Lock,
  Key,

  // Misc
  Bell,
  Search,
  Calendar,
  Clock,
  Clock3,
  CheckCircle,
  AlertTriangle,
  Info,
  HelpCircle,
  type LucideIcon,
} from "lucide-react";

/**
 * Icon registry mapping string names to Lucide components
 * Add new icons here when needed for module configuration
 */
export const ICON_REGISTRY: Record<string, LucideIcon> = {
  // Navigation & Layout
  LayoutDashboard,
  LayoutGrid,
  Menu,
  Home,

  // Commerce
  ShoppingCart,
  ShoppingBag,
  CreditCard,
  DollarSign,
  Receipt,
  Percent,
  Award,
  Crown,
  Undo2,

  // Inventory & Products
  Package,
  PackageCheck,
  Boxes,
  Pill,
  FlaskConical,
  ClipboardList,
  ClipboardCheck,
  SlidersHorizontal,
  CalendarClock,
  TriangleAlert,

  // Documents & Files
  FileText,
  File,
  Files,
  Folder,
  ScrollText,
  FileUp,

  // People & Organizations
  Users,
  UserCog,
  User,
  Building,
  Building2,
  Factory,
  Store,
  Stethoscope,

  // Transport & Logistics
  Truck,
  ArrowRightLeft,
  MapPin,

  // Charts & Reports
  BarChart,
  BarChart3,
  TrendingUp,
  PieChart,

  // Settings & Admin
  Settings,
  Shield,
  ShieldCheck,
  Lock,
  Key,

  // Misc
  Bell,
  Search,
  Calendar,
  Clock,
  Clock3,
  CheckCircle,
  AlertTriangle,
  Info,
  HelpCircle,
};

/**
 * Get a Lucide icon component by name
 * @param iconName - The string name of the icon (e.g., "ShoppingCart")
 * @param fallback - Optional fallback icon if not found (defaults to HelpCircle)
 * @returns The Lucide icon component
 */
export function getIcon(
  iconName: string | undefined,
  fallback: LucideIcon = HelpCircle,
): LucideIcon {
  if (!iconName) return fallback;
  return ICON_REGISTRY[iconName] || fallback;
}

/**
 * Check if an icon name exists in the registry
 */
export function isValidIcon(iconName: string): boolean {
  return iconName in ICON_REGISTRY;
}

/**
 * Get all available icon names for UI pickers
 */
export function getAvailableIcons(): string[] {
  return Object.keys(ICON_REGISTRY);
}

export type IconName = keyof typeof ICON_REGISTRY;
